/**
 * Whether this page is running inside the desktop chat app.
 *
 * TalkVibe on a desktop is **the chat and nothing else** — the shape LINE has
 * on a PC. It is the same web app, loaded by a small native window, because a
 * second implementation of a transcript is how two implementations drift; what
 * differs is how much of the product that window shows. A person who opened a
 * chat window did not ask for the feed, the marketplace or stories, and
 * navigating into them from there would turn a chat app back into a browser.
 *
 * **Detected from the user agent**, because that is the one signal every shell
 * can set and every navigation carries: an Electron or Tauri window appends a
 * token to its user agent string, and it survives a reload, a deep link and a
 * sign-out without a query parameter trailing through every URL.
 *
 * Nothing about this is a permission. It decides which navigation is drawn, and
 * a browser pretending to be the desktop app gets a narrower menu and nothing
 * else — every rule that matters is on the server, keyed to the session.
 */
const TOKEN = 'TalkVibeDesktop';

export function isDesktopShell(): boolean {
  if (typeof navigator === 'undefined') return false;
  return navigator.userAgent.includes(TOKEN);
}

/** The token a shell appends to its user agent. Exported so both ends agree. */
export const DESKTOP_SHELL_TOKEN = TOKEN;

/**
 * The desktop app on a Mac, where the window has no title bar of its own.
 *
 * The shell hides it so the app's own header reaches the top of the window,
 * which is the shape a chat app has on macOS — and that leaves Apple's three
 * window buttons floating over whatever is in the top-left corner. The layout
 * reserves room for them; nothing else needs to know.
 */
export function isDesktopMac(): boolean {
  return isDesktopShell() && navigator.userAgent.includes('Macintosh');
}
